import { useCallback, useEffect, useRef, useState } from "react";

// The three LIFT entry points share the ordinary client form. Saving is an
// explicit action; opening or inspecting an order never creates a record.
export default function useLiftClientRegistration({ identity, canOpen, onCreated }) {
  const [registration, setRegistration] = useState(null);
  const pending = useRef(null);
  const callbacks = useRef({ canOpen, onCreated });
  callbacks.current = { canOpen, onCreated };
  const cancelRegistration = useCallback(() => {
    const current = pending.current;
    pending.current = null;
    current?.controller?.abort();
    current?.resolve(null);
    setRegistration(null);
  }, []);
  useEffect(() => {
    cancelRegistration();
    return cancelRegistration;
  }, [identity, cancelRegistration]);

  const requestRegistration = useCallback(target => {
    if (!target?.draft || !target.storeId || !target.pedido) {
      return Promise.reject(new Error("Não foi possível carregar o cadastro desta venda."));
    }
    if (!callbacks.current.canOpen()) {
      return Promise.reject(new Error("Conclua o atendimento aberto antes de cadastrar outro cliente."));
    }
    cancelRegistration();
    return new Promise(resolve => {
      const request = { ...target, identity, resolve };
      pending.current = request;
      setRegistration(request);
    });
  }, [identity, cancelRegistration]);

  const saveRegistration = useCallback(async submitted => {
    const request = pending.current;
    if (!request || request.identity !== identity) throw new Error("O cadastro não está mais aberto.");
    if (request.saving) throw new Error("O cadastro já está sendo salvo.");
    const origemCliente = submitted?.origemCliente;
    if (!["lead", "porta"].includes(origemCliente)) {
      throw new Error("Selecione de onde veio este cliente antes de salvar.");
    }
    const draft = submitted && typeof submitted === "object" && !Array.isArray(submitted)
      ? { ...submitted } : {};
    delete draft.origemCliente;
    if (Object.prototype.hasOwnProperty.call(request.draft, "origin")) {
      draft.origin = request.draft.origin;
    } else {
      delete draft.origin;
    }
    request.saving = true;
    request.controller = new AbortController();
    try {
      const endpoint = `/api/lift/pedidos/${encodeURIComponent(request.storeId)}/${encodeURIComponent(request.pedido)}`;
      const lookupResponse = await fetch(`${endpoint}/atendimento-crm`, {
        method:"GET",
        credentials:"same-origin",
        cache:"no-store",
        headers:{ Accept:"application/json" },
        signal:request.controller.signal,
      });
      const lookup = await lookupResponse.json().catch(() => ({}));
      if (!lookupResponse.ok) {
        throw new Error(lookup.error || `Não foi possível confirmar a atualização do servidor (HTTP ${lookupResponse.status})`);
      }
      if (pending.current !== request) throw new Error("A visualização mudou durante a confirmação do servidor.");
      if (lookup.originRegistrationVersion !== 1) {
        throw new Error("O servidor ainda não está atualizado para cadastrar clientes com origem. Nenhum cadastro foi enviado; aguarde a atualização e tente novamente.");
      }
      if (lookup.state !== "missing") {
        throw new Error("O atendimento desta venda mudou. Atualize a tela antes de tentar cadastrar o cliente.");
      }
      const response = await fetch(`${endpoint}/cadastrar-cliente`, {
        method:"POST", credentials:"same-origin",
        headers:{ "Content-Type":"application/json" },
        body:JSON.stringify({ draft, origemCliente }), signal:request.controller.signal,
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result?.client?.id) {
        throw new Error(result.error || "Não foi possível cadastrar o cliente. Tente novamente.");
      }
      if (pending.current !== request) throw new Error("A visualização mudou durante o cadastro.");
      const savedClient = {
        ...result.client,
        originCliente:result.client.originCliente ?? result.originCliente ?? origemCliente,
        ...(result.sourceKind || result.client.sourceKind ? { sourceKind:result.client.sourceKind || result.sourceKind } : {}),
        ...(result.sourceLink || result.client.sourceLink ? { sourceLink:result.client.sourceLink || result.sourceLink } : {}),
        ...(result.client.originReviewLabel || result.originReviewLabel
          ? { originReviewLabel:result.client.originReviewLabel || result.originReviewLabel } : {}),
      };
      await callbacks.current.onCreated(savedClient, { sale:result.sale });
      pending.current = null;
      setRegistration(null);
      request.resolve(savedClient);
      return savedClient;
    } finally {
      request.saving = false;
    }
  }, [identity]);
  return { registration, requestRegistration, cancelRegistration, saveRegistration };
}